import { formatCents, type Invoice } from '@groundly/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';

import { useInvoice, useStartInvoicePayment } from '../../../../../api/billing.js';
import { isNotFound, isPermissionDenied } from '../../../../../api/call.js';
import { usePermissions } from '../../../../../api/me.js';
import {
  formatCycle,
  formatDate,
  InvoiceStatusBadge,
  number,
} from '../../../../../billing/BillingSections.js';
import styles from '../../../../../billing/billing.module.css';
import { openPaddleCheckout, returnUrl } from '../../../../../billing/paddle-checkout.js';
import { Button } from '../../../../../components/Button.js';
import { DangerAlert } from '../../../../../components/DangerAlert.js';
import { InfoAlert } from '../../../../../components/InfoAlert.js';
import { PageHeading } from '../../../../../components/PageHeading.js';
import { LoadingArea, TextPage } from '../../../../../components/PlaceholderPage.js';
import { PermissionDeniedPage } from '../../../../../components/SectionStates.js';

/**
 * `/account/billing/invoices/:invoiceId`: one invoice, with a line for each
 * website: its plan, the days billed (a website added mid-cycle pays only
 * for its days), the AI replies used and any overage. An unpaid invoice can
 * be paid by card: Paddle's checkout opens over the page and sends the payer
 * back with `?payment=done`, and the page waits for Paddle to confirm it.
 */
const searchSchema = z.object({ payment: z.enum(['done']).optional() });

export const Route = createFileRoute('/_app/account/billing/invoices/$invoiceId')({
  validateSearch: (search: Record<string, unknown>) => {
    const parsed = searchSchema.safeParse(search);
    return parsed.success ? parsed.data : {};
  },
  component: InvoiceRoute,
});

const PERMISSION = 'billing and invoices';

function InvoiceRoute() {
  const permissions = usePermissions();
  if (!permissions.includes('billing_and_invoices')) {
    return <PermissionDeniedPage title="Invoice" permission={PERMISSION} />;
  }
  return <InvoicePage />;
}

function BackLink() {
  return (
    <Link to="/account/billing" className={styles.back}>
      <ArrowLeft aria-hidden="true" width={14} height={14} strokeWidth={1.75} />
      Billing
    </Link>
  );
}

function InvoicePage() {
  const { invoiceId } = Route.useParams();
  const returnedFromPayment = Route.useSearch().payment === 'done';
  const invoice = useInvoice(invoiceId, { awaitingPayment: returnedFromPayment });

  if (invoice.isError && isPermissionDenied(invoice.error)) {
    return <PermissionDeniedPage title="Invoice" permission={PERMISSION} />;
  }
  if (invoice.isError && isNotFound(invoice.error)) {
    return (
      <TextPage title="Invoice" action={<BackLink />}>
        This invoice doesn&apos;t exist.
      </TextPage>
    );
  }

  return (
    <>
      <BackLink />
      <PageHeading documentTitle={invoice.data ? `Invoice ${invoice.data.number}` : 'Invoice'}>
        {invoice.data ? `Invoice ${invoice.data.number}` : 'Invoice'}
      </PageHeading>
      <div className={styles.stack}>
        {invoice.isError ? (
          <DangerAlert
            title="Couldn't load this invoice"
            action={<Button onClick={() => void invoice.refetch()}>Try again</Button>}
          >
            Check your connection and try again.
          </DangerAlert>
        ) : invoice.isPending ? (
          <LoadingArea />
        ) : (
          <InvoiceBody invoice={invoice.data} returnedFromPayment={returnedFromPayment} />
        )}
      </div>
    </>
  );
}

/** Opens Paddle's checkout for the invoice's total over the page. */
function PayByCard({ invoice }: { invoice: Invoice }) {
  const start = useStartInvoicePayment(invoice.id);
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);
  const error = start.error?.message ?? openError;
  return (
    <>
      {error !== null ? (
        <DangerAlert title="Couldn't open the payment page">
          {error || 'Check your connection and try again.'}
        </DangerAlert>
      ) : null}
      <div>
        <Button
          variant="primary"
          disabled={start.isPending || opening}
          onClick={() => {
            setOpenError(null);
            start.mutate(undefined, {
              onSuccess: (checkout) => {
                setOpening(true);
                openPaddleCheckout(checkout, returnUrl('payment', 'done'))
                  .catch((e: unknown) => setOpenError(e instanceof Error ? e.message : String(e)))
                  .finally(() => setOpening(false));
              },
            });
          }}
        >
          {start.isPending || opening
            ? 'Opening the payment page…'
            : `Pay ${formatCents(invoice.totalCents)} by card`}
        </Button>
      </div>
    </>
  );
}

function InvoiceBody({
  invoice,
  returnedFromPayment,
}: {
  invoice: Invoice;
  returnedFromPayment: boolean;
}) {
  const lineOff = (line: Invoice['lines'][number]) => line.discountCents + line.creditCents;
  const lineDiscounts = invoice.lines.some((line) => lineOff(line) > 0);
  const invoiceDiscounts = invoice.discounts.filter((d) => d.websiteId === null);
  // Website discounts by website, for the line's note: "Early customer, 20% off".
  const notes = (websiteId: string) =>
    invoice.discounts
      .filter((d) => d.websiteId === websiteId)
      .map((d) => `${d.label}, ${d.description}`)
      .join('; ');
  const linesTotal = invoice.lines.reduce((sum, line) => sum + line.totalCents, 0);
  const columns = lineDiscounts ? 7 : 6;
  return (
    <>
      <div className={styles.meta}>
        <InvoiceStatusBadge status={invoice.status} />
        <span>Billing cycle {formatCycle(invoice.periodStart, invoice.periodEnd)}</span>
        <span className={styles.muted}>Issued {formatDate(invoice.issuedAt)}</span>
        {invoice.status === 'unpaid' ? (
          <span className={styles.muted}>Due {formatDate(invoice.dueOn)}</span>
        ) : null}
        {invoice.status === 'paid' && invoice.paidAt ? (
          <span className={styles.muted}>
            Paid {formatDate(invoice.paidAt)}
            {invoice.paymentReference ? ` · ${invoice.paymentReference}` : ''}
          </span>
        ) : null}
      </div>

      {returnedFromPayment && invoice.status === 'unpaid' ? (
        <InfoAlert title="Confirming your payment">
          This invoice shows as paid as soon as the payment is confirmed, usually within a minute.
        </InfoAlert>
      ) : invoice.autopayPending ? (
        <InfoAlert title="Charged to your card">
          This invoice was charged to your card on file. It shows as paid as soon as the payment is
          confirmed, usually within a minute.
        </InfoAlert>
      ) : invoice.payableByCard ? (
        <PayByCard invoice={invoice} />
      ) : null}

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <caption className="visually-hidden">Charges for each website</caption>
          <thead>
            <tr>
              <th scope="col">Website</th>
              <th scope="col">Plan</th>
              <th scope="col">Days billed</th>
              <th scope="col">Plan charge</th>
              <th scope="col">AI replies</th>
              <th scope="col">Overage</th>
              {lineDiscounts ? <th scope="col">Credits and discounts</th> : null}
              <th scope="col">Total</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines.map((line) => {
              const prorated = line.billedDays < line.periodDays;
              return (
                <tr key={line.websiteId}>
                  <th scope="row">{line.websiteName}</th>
                  <td>
                    {line.planName}
                    <div className={styles.muted}>
                      {line.pricingType === 'founding' ? (
                        <>
                          <s>{formatCents(line.listPriceCents)}</s> {formatCents(line.priceCents)}{' '}
                          founding
                          {line.billingInterval === 'yearly' ? ' / year' : ''}
                        </>
                      ) : (
                        `${formatCents(line.priceCents)} / ${line.billingInterval === 'yearly' ? 'year' : 'month'}`
                      )}
                    </div>
                  </td>
                  <td className={styles.nowrap}>
                    {line.billedDays} of {line.periodDays}
                    {prorated ? (
                      <div className={styles.muted}>from {formatDate(line.billedFrom)}</div>
                    ) : null}
                  </td>
                  <td>
                    {formatCents(line.baseCents)}
                    {prorated ? <div className={styles.muted}>prorated</div> : null}
                    {line.billingInterval === 'yearly' ? (
                      <div className={styles.muted}>
                        {line.baseCents > 0 ? 'yearly fee' : 'paid yearly'}
                      </div>
                    ) : null}
                  </td>
                  <td className={styles.nowrap}>
                    {number(line.messagesUsed)}
                    <div className={styles.muted}>{number(line.includedMessages)} included</div>
                  </td>
                  <td className={styles.nowrap}>
                    {formatCents(line.overageCents)}
                    {line.overageMessages > 0 ? (
                      <div className={styles.muted}>
                        {/* The rate this invoice was billed at, not today's. */}
                        {number(line.overageMessages)} over
                        {line.cappedAtPlanName
                          ? ` · capped at the ${line.cappedAtPlanName} price`
                          : line.overageCentsPerReply > 0
                            ? ` · ${line.overageCentsPerReply}¢ each`
                            : ''}
                      </div>
                    ) : null}
                  </td>
                  {lineDiscounts ? (
                    <td>
                      {lineOff(line) > 0 ? (
                        <>
                          −{formatCents(lineOff(line))}
                          <div className={styles.muted}>
                            {[
                              line.creditCents > 0
                                ? `Credit for the unused months of the yearly plan it replaced, ${formatCents(line.creditCents)}`
                                : '',
                              notes(line.websiteId),
                            ]
                              .filter(Boolean)
                              .join('; ')}
                          </div>
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                  ) : null}
                  <td>
                    <strong>{formatCents(line.totalCents)}</strong>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            {invoiceDiscounts.length > 0 ? (
              <>
                <tr className={styles.subtotal}>
                  <th scope="row" colSpan={columns}>
                    Subtotal
                  </th>
                  <td>{formatCents(linesTotal)}</td>
                </tr>
                {invoiceDiscounts.map((discount, index) => (
                  <tr key={index} className={styles.subtotal}>
                    <th scope="row" colSpan={columns}>
                      {discount.label}
                      <span className={styles.muted}> · {discount.description}</span>
                    </th>
                    <td>−{formatCents(discount.amountCents)}</td>
                  </tr>
                ))}
              </>
            ) : null}
            <tr className={styles.totals}>
              <th scope="row" colSpan={3}>
                Total
              </th>
              <td>{formatCents(invoice.baseCents)}</td>
              <td />
              <td>{formatCents(invoice.overageCents)}</td>
              {lineDiscounts ? (
                <td>−{formatCents(invoice.discountCents + invoice.creditCents)}</td>
              ) : null}
              <td>{formatCents(invoice.totalCents)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className={styles.muted}>
        {invoice.payableByCard
          ? 'Amounts in US dollars, before any sales tax added at checkout. Pay by card above, or with the payment details that come with the invoice; Groundly marks it paid once payment arrives.'
          : 'Amounts in US dollars. Payment details come with the invoice, and Groundly marks it paid once payment arrives.'}
      </p>
    </>
  );
}
